import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { NumberFieldWithBackground } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const TipsGroupCustomTip = (props: TipsGroupCustomTipProps) => {
  const { selectedTip, maxTipAmount, onChange } = props;

  const { formatMessage, formatNumber } = useIntl();

  // NOTE: We use cents for tip calculations and dollar values for presentation
  const displayTipAmount = selectedTip / 100;
  const hasExceedMaxTipAmount = selectedTip > maxTipAmount;

  const customTipNoticeText = formatMessage(messages.maximumReached, {
    max: formatNumber(maxTipAmount / 100, {
      style: 'currency',
      currency: 'USD',
    }),
  });

  const customTipNotice = hasExceedMaxTipAmount
    ? customTipNoticeText
    : undefined;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onCustomTipAmountChange = useCallback(
    (tipAmountNumericValue: string) => {
      const tipAmountAsNumber = Number(tipAmountNumericValue);
      const isValidTipAmount = validateCustomTipAmount(tipAmountAsNumber);

      const tipAmountAsNumberInCents = tipAmountAsNumber * 100;
      const tipAmountWithFallback = isValidTipAmount
        ? tipAmountAsNumberInCents
        : 0;

      onChange(tipAmountWithFallback);
    },
    [onChange],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <NumberFieldWithBackground
      accessibilityLabel={formatMessage(messages.customField)}
      placeholder={formatMessage(messages.customField)}
      value={displayTipAmount <= 0 ? undefined : displayTipAmount}
      onChangeText={onCustomTipAmountChange}
      min={0}
      maxDecimals={2}
      invalid={hasExceedMaxTipAmount}
      noticePalette="caution"
      notice={customTipNotice}
      textPrepend="$"
      autoFocus
      returnKeyType="done"
    />
  );
};

// ─── Utils ───────────────────────────────────────────────────────────────────

function validateCustomTipAmount(tipAmount: number) {
  return Number.isFinite(tipAmount) && tipAmount > 0;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  customField: {
    defaultMessage: 'Enter custom tip',
    description: 'Bag | Tipping | Custom field',
  },
  maximumReached: {
    defaultMessage:
      'The maximum tip for this order is {max}, but our team appreciates your generosity!',
    description: 'Bag | Tipping | Maximum reached',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type TipsGroupCustomTipProps = Readonly<{
  selectedTip: number;
  maxTipAmount: number;
  onChange: (topAmount: number) => void;
}>;
